/**
 * Alerts Management
 *
 * Alerts (watched items for notifications) require login. Anonymous users
 * cannot create them - a soft anonymous toggle was tried and reverted (see
 * git history) because it let the bell show "Notified" before anyone was
 * actually subscribed. main.ts gates the click itself (requestAlertSignIn)
 * before this module is ever asked to toggle anything for a logged-out
 * user, and auto-applies the attempted alert immediately after sign-in
 * completes (applyPendingAlertIfAny) so the user never has to click twice.
 *
 * Note: This stores the alert subscriptions. Actual notification
 * delivery is handled by a backend service.
 */

import type { DataClient } from '@slowmaui/api-client';

type AlertsChangeCallback = (alerts: Set<string>) => void;

let dataClient: DataClient | null = null;
let isLoggedIn = false;
let alerts: Set<string> = new Set();
let listeners: AlertsChangeCallback[] = [];

type AlertItemType = 'property' | 'neighborhood' | 'district' | 'region';

/**
 * Parse item key to extract itemId and itemType
 * Properties: TMK normalized string (e.g., "2-4-2-019-024-0001")
 * Neighborhoods: "nh:id" format
 * Districts: "d:id" format
 * Regions: "r:id" format
 */
function parseItemKey(key: string): { itemId: string; itemType: AlertItemType } {
  if (key.startsWith('nh:')) {
    return { itemId: key.substring(3), itemType: 'neighborhood' };
  }
  if (key.startsWith('d:')) {
    return { itemId: key.substring(2), itemType: 'district' };
  }
  if (key.startsWith('r:')) {
    return { itemId: key.substring(2), itemType: 'region' };
  }
  return { itemId: key, itemType: 'property' };
}

/**
 * Build item key from itemId and itemType
 */
function buildItemKey(itemId: string, itemType: AlertItemType): string {
  if (itemType === 'neighborhood') return `nh:${itemId}`;
  if (itemType === 'district') return `d:${itemId}`;
  if (itemType === 'region') return `r:${itemId}`;
  return itemId;
}

/**
 * Notify all listeners of alerts change
 */
function notifyListeners(): void {
  listeners.forEach(callback => callback(new Set(alerts)));
}

/**
 * Initialize alerts for anonymous user (empty - alerts require login)
 */
export function initAlerts(): void {
  isLoggedIn = false;
  dataClient = null;
  alerts = new Set();
  notifyListeners();
}

/**
 * Initialize alerts for logged-in user (API)
 * @param client - DataClient instance with auth token set
 * @param serverAlerts - Alerts loaded from server
 */
export function initAlertsForUser(
  client: DataClient,
  serverAlerts: Array<{ itemId: string; itemType: AlertItemType }>
): void {
  dataClient = client;
  isLoggedIn = true;
  alerts = new Set(serverAlerts.map(a => buildItemKey(a.itemId, a.itemType)));
  notifyListeners();
}

/**
 * Reset to anonymous mode (on logout)
 */
export function resetAlertsToAnonymous(): void {
  isLoggedIn = false;
  dataClient = null;
  alerts = new Set();
  notifyListeners();
}

/**
 * Get all alerts
 */
export function getAlerts(): Set<string> {
  return new Set(alerts);
}

/**
 * Check if item has an alert
 */
export function hasAlert(id: string): boolean {
  return alerts.has(id);
}

/**
 * Check if user is logged in (alerts require login)
 */
export function canCreateAlerts(): boolean {
  return isLoggedIn;
}

/**
 * Toggle alert status
 * @returns The new alert state, or throws if not logged in
 */
export async function toggleAlert(id: string): Promise<boolean> {
  if (!isLoggedIn || !dataClient) {
    throw new Error('Login required to create alerts');
  }

  const wasAlerted = alerts.has(id);
  const { itemId, itemType } = parseItemKey(id);

  if (wasAlerted) {
    alerts.delete(id);
  } else {
    alerts.add(id);
  }

  // Optimistic update - notify immediately
  notifyListeners();

  try {
    if (wasAlerted) {
      await dataClient.removeAlert(itemId);
    } else {
      await dataClient.addAlert(itemId, itemType);
    }
  } catch (error) {
    // Revert on error
    console.error('Failed to update alert:', error);
    if (wasAlerted) {
      alerts.add(id);
    } else {
      alerts.delete(id);
    }
    notifyListeners();
    throw error;
  }

  return alerts.has(id);
}

/**
 * Add alert
 * @throws If not logged in
 */
export async function addAlert(id: string): Promise<void> {
  if (!isLoggedIn || !dataClient) {
    throw new Error('Login required to create alerts');
  }

  if (alerts.has(id)) return;

  const { itemId, itemType } = parseItemKey(id);
  alerts.add(id);
  notifyListeners();

  try {
    await dataClient.addAlert(itemId, itemType);
  } catch (error) {
    console.error('Failed to add alert:', error);
    alerts.delete(id);
    notifyListeners();
    throw error;
  }
}

/**
 * Remove alert
 * @throws If not logged in
 */
export async function removeAlert(id: string): Promise<void> {
  if (!isLoggedIn || !dataClient) {
    throw new Error('Login required to remove alerts');
  }

  if (!alerts.has(id)) return;

  const { itemId } = parseItemKey(id);
  alerts.delete(id);
  notifyListeners();

  try {
    await dataClient.removeAlert(itemId);
  } catch (error) {
    console.error('Failed to remove alert:', error);
    alerts.add(id);
    notifyListeners();
    throw error;
  }
}

/**
 * Subscribe to alerts changes
 */
export function onAlertsChange(callback: AlertsChangeCallback): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter(l => l !== callback);
  };
}

/**
 * Get alerts count
 */
export function getAlertsCount(): number {
  return alerts.size;
}

/**
 * Check if currently using API (logged in)
 */
export function isUsingApi(): boolean {
  return isLoggedIn;
}
